'use client'

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useAdmin } from '@/hooks/useAdmin'
import { Button } from '@/components/ui/Button'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { ArrowLeft, Search, Filter, Calendar, MessageCircle, Clock, ChevronLeft, ChevronRight, Eye, EyeOff, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import SEO from '@/components/layout/SEO'

interface Conversation {
  id: string
  title: string
  updated_at: string
  created_at: string
  categories: {
    name: string
    icon: string
  }
}

interface Category {
  id: string
  name: string
  icon: string
}

export default function ConversationsPage() {
  const navigate = useNavigate()
  const { user, userProfile, isLoading: authLoading } = useAuth()
  const { isAdmin } = useAdmin()

  // Safeguarding & Enclosure: Homeschool parents and children belong in their dedicated education hubs
  useEffect(() => {
    if (authLoading || !userProfile) return
    const fType = userProfile.facility_type
    const role = userProfile.facility_role
    if (fType === 'homeschool' || fType === 'family_edu') {
      if (role === 'admin') {
        navigate('/homeschool/parent', { replace: true })
      } else {
        navigate('/learn', { replace: true })
      }
    } else if (role === 'student') {
      navigate('/learn', { replace: true })
    }
  }, [userProfile, authLoading, navigate])
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [filteredConversations, setFilteredConversations] = useState<Conversation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [categories, setCategories] = useState<Category[]>([])
  const [lastUserSnippets, setLastUserSnippets] = useState<Record<string, string>>({})
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState<string>('')
  const [showCategoryBar, setShowCategoryBar] = useState(true)
  const [hiddenCategoryIds, setHiddenCategoryIds] = useState<string[]>([])
  const [showHiddenModal, setShowHiddenModal] = useState(false)

  const categoryScrollRef = useRef<HTMLDivElement>(null)

  const supabase = createClientComponentClient()
  const didFetchRef = useRef(false)
  const loadedSnippetsRef = useRef(false)

  // Load hidden categories from localStorage
  useEffect(() => {
    if (user) {
      try {
        const raw = localStorage.getItem(`hidden_categories_${user.id}`)
        const parsed = JSON.parse(raw || '[]')
        if (Array.isArray(parsed)) setHiddenCategoryIds(parsed)
      } catch { /* ignore */ }
    }
  }, [user])

  const persistHidden = (next: string[]) => {
    setHiddenCategoryIds(next)
    if (user) {
      localStorage.setItem(`hidden_categories_${user.id}`, JSON.stringify(next))
    }
  }

  const hideCategory = (categoryId: string) => {
    if (hiddenCategoryIds.includes(categoryId)) return
    persistHidden([...hiddenCategoryIds, categoryId])
  }

  const unhideCategory = (categoryId: string) => {
    if (!hiddenCategoryIds.includes(categoryId)) return
    persistHidden(hiddenCategoryIds.filter(id => id !== categoryId))
  }

  const visibleCategories = categories.filter(c => !hiddenCategoryIds.includes(c.id))
  const hiddenCategories = categories.filter(c => hiddenCategoryIds.includes(c.id))

  const fetchConversations = useCallback(async () => {
    if (!user) return

    try {
      setIsLoading(false)

      const { data: conversationsData, error: conversationsError } = await supabase
        .from('conversations')
        .select(`
          id,
          title,
          updated_at,
          created_at,
          categories!inner (
            name,
            icon
          )
        `)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(50)

      if (conversationsError) throw conversationsError

      setConversations((conversationsData as any) || [])
      setFilteredConversations((conversationsData as any) || [])

      if (!loadedSnippetsRef.current && Array.isArray(conversationsData) && conversationsData.length) {
        loadedSnippetsRef.current = true

        setTimeout(async () => {
          const limited = conversationsData.slice(0, 20)
          const snippets: Record<string, string> = {}

          try {
            const batchSize = 5
            for (let i = 0; i < limited.length; i += batchSize) {
              const batch = limited.slice(i, i + batchSize)
              await Promise.all(
                batch.map(async (conv) => {
                  const { data: msg } = await supabase
                    .from('messages')
                    .select('content')
                    .eq('conversation_id', conv.id)
                    .eq('role', 'user')
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle()
                  if (msg?.content) {
                    const text = String(msg.content)
                    snippets[conv.id] = text.length > 120 ? text.slice(0, 117) + '…' : text
                  }
                })
              )
              setLastUserSnippets(prev => ({ ...prev, ...snippets }))
            }
          } catch (_) {
            // ignore
          }
        }, 100)
      }

      let query = supabase.from('categories').select('id, name, icon').order('name')
      if (!isAdmin) query = query.eq('is_visible', true)

      query.then(({ data }: { data: any }) => {
        if (data) setCategories(data)
      })

    } catch (error) {
      console.error('Error fetching conversations:', error)
    }
  }, [user, supabase])

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth/login')
      return
    }

    // Skip companion conversation fetch if enclosed homeschool user being redirected
    const fType = userProfile?.facility_type
    const role = userProfile?.facility_role
    if (fType === 'homeschool' || fType === 'family_edu' || role === 'student') {
      return
    }

    if (user && !didFetchRef.current) {
      didFetchRef.current = true
      fetchConversations()
    }
  }, [user, userProfile, authLoading, navigate, fetchConversations])

  // Realtime subscription for conversations
  useEffect(() => {
    if (!user?.id) return

    const channel = supabase
      .channel(`conversations-list-${user.id}`)
      .on('postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversations',
          filter: `user_id=eq.${user.id}`
        },
        (payload: any) => {
          fetchConversations()
        }
      )
      .on('postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
          filter: `user_id=eq.${user.id}`
        },
        (payload: any) => {
          fetchConversations()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id, fetchConversations, supabase])

  // Filter conversations based on search and category
  useEffect(() => {
    let filtered = conversations

    if (searchTerm) {
      filtered = filtered.filter(conv =>
        conv.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        conv.categories.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(conv => conv.categories.name === selectedCategory)
    }

    setFilteredConversations(filtered)
  }, [conversations, searchTerm, selectedCategory])

  const handleConversationClick = (conversationId: string) => {
    navigate(`/conversation/${conversationId}`)
  }

  const startEditing = (conv: Conversation) => {
    setEditingId(conv.id)
    setEditingTitle(conv.title || '')
  }

  const saveTitle = async (conv: Conversation) => {
    if (!editingTitle || editingTitle === conv.title) {
      setEditingId(null)
      return
    }
    await supabase
      .from('conversations')
      .update({ title: editingTitle })
      .eq('id', conv.id)
    setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, title: editingTitle } : c))
    setFilteredConversations(prev => prev.map(c => c.id === conv.id ? { ...c, title: editingTitle } : c))
    setEditingId(null)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else if (diffInHours < 168) {
      return date.toLocaleDateString([], { weekday: 'short' })
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    }
  }

  const scrollCategories = (direction: 'left' | 'right') => {
    if (categoryScrollRef.current) {
      const scrollAmount = 200
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#faf7f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: '#faf7f2', paddingBottom: '80px' }}>
      <SEO title="Conversations" noindex />
      {/* Sage-themed Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        style={{
          background: 'white',
          borderBottom: '1px solid #f0ebe0',
          boxShadow: '0 1px 8px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px 24px' }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                style={{
                  background: '#faf7f2',
                  border: '1px solid #f0ebe0',
                  borderRadius: '10px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  color: '#4a5568',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Dashboard</span>
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center"
                style={{
                  width: '44px', height: '44px',
                  background: 'linear-gradient(135deg, #4a7c5e 0%, #2d5a3d 100%)',
                  borderRadius: '14px',
                  boxShadow: '0 3px 12px rgba(74,124,94,0.3)',
                  fontSize: '22px',
                }}
              >
                <img src="/icons/blue-sage-logo.png" alt="Sage" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
              </div>
              <div>
                <h1 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#1a2740',
                  fontFamily: "'Playfair Display', 'Georgia', serif",
                }}>
                  Conversation History
                </h1>
                <p style={{ fontSize: '14px', color: '#4a5568' }}>
                  {conversations.length} conversations
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Category Slider Bar */}
      <AnimatePresence>
        {showCategoryBar && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{
              background: 'white',
              borderBottom: '1px solid #f0ebe0',
              overflow: 'hidden',
            }}
          >
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '12px 24px' }}>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => scrollCategories('left')}
                  className="flex-shrink-0 flex items-center justify-center hover:bg-gray-100 transition-colors hidden sm:flex"
                  style={{ width: '32px', height: '32px', borderRadius: '8px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#4a5568' }}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div
                  ref={categoryScrollRef}
                  className="flex gap-2 overflow-x-auto flex-1"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  <style>{`.sage-cat-scroll::-webkit-scrollbar { display: none; }`}</style>
                  <div className="sage-cat-scroll flex gap-2 min-w-0 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                    {/* All chip */}
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className="flex items-center gap-2 flex-shrink-0 transition-all duration-200"
                      style={{
                        padding: '9px 16px',
                        borderRadius: '50px',
                        border: selectedCategory === 'all' ? '2px solid #4a7c5e' : '2px solid transparent',
                        background: selectedCategory === 'all' ? '#4a7c5e' : '#faf7f2',
                        color: selectedCategory === 'all' ? 'white' : '#4a5568',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        boxShadow: selectedCategory === 'all' ? '0 3px 12px rgba(74,124,94,0.3)' : 'none',
                      }}
                    >
                      <span>📋</span> All
                    </button>

                    {visibleCategories.map((cat) => (
                      <div key={cat.id} className="relative group flex-shrink-0">
                        <button
                          onClick={() => setSelectedCategory(cat.name === selectedCategory ? 'all' : cat.name)}
                          className="flex items-center gap-2 transition-all duration-200"
                          style={{
                            padding: '9px 16px',
                            borderRadius: '50px',
                            border: selectedCategory === cat.name ? '2px solid #4a7c5e' : '2px solid transparent',
                            background: selectedCategory === cat.name ? '#4a7c5e' : '#faf7f2',
                            color: selectedCategory === cat.name ? 'white' : '#4a5568',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            boxShadow: selectedCategory === cat.name ? '0 3px 12px rgba(74,124,94,0.3)' : 'none',
                          }}
                        >
                          <span style={{ fontSize: '16px' }}>{cat.icon}</span>
                          {cat.name}
                        </button>
                        {/* Hide button on hover */}
                        <button
                          onClick={(e) => { e.stopPropagation(); hideCategory(cat.id) }}
                          className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{
                            width: '18px', height: '18px',
                            borderRadius: '50%',
                            background: '#ef4444',
                            border: '2px solid white',
                            color: 'white',
                            fontSize: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                          }}
                          title="Hide this category"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => scrollCategories('right')}
                  className="flex-shrink-0 flex items-center justify-center hover:bg-gray-100 transition-colors hidden sm:flex"
                  style={{ width: '32px', height: '32px', borderRadius: '8px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#4a5568' }}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

                {/* Manage hidden / Toggle bar */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {hiddenCategories.length > 0 && (
                    <button
                      onClick={() => setShowHiddenModal(true)}
                      className="flex items-center gap-1 hover:opacity-80 transition-opacity"
                      style={{
                        padding: '6px 10px',
                        borderRadius: '8px',
                        border: '1px solid #f0ebe0',
                        background: '#faf7f2',
                        color: '#c9903a',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <EyeOff className="w-3 h-3" />
                      {hiddenCategories.length}
                    </button>
                  )}
                  <button
                    onClick={() => setShowCategoryBar(false)}
                    className="flex items-center justify-center hover:bg-gray-100 transition-colors"
                    style={{
                      width: '28px', height: '28px',
                      borderRadius: '6px',
                      border: '1px solid #f0ebe0',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: '#4a5568',
                    }}
                    title="Hide category bar"
                  >
                    <EyeOff className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Show category bar toggle when hidden */}
      {!showCategoryBar && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{ background: 'white', borderBottom: '1px solid #f0ebe0', padding: '8px 24px', textAlign: 'center' }}
        >
          <button
            onClick={() => setShowCategoryBar(true)}
            className="flex items-center gap-1.5 mx-auto hover:opacity-80 transition-opacity"
            style={{
              padding: '6px 14px',
              borderRadius: '50px',
              border: '1px solid #b8d9c4',
              background: '#e8f2ec',
              color: '#2d5a3d',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            <Eye className="w-3.5 h-3.5" />
            Show categories
          </button>
        </motion.div>
      )}

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px 16px' }}>
        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            border: '1px solid #f0ebe0',
          }}
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search
                className="absolute top-1/2 -translate-y-1/2"
                style={{ left: '14px', width: '18px', height: '18px', color: '#4a5568', opacity: 0.5 }}
              />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '48px',
                  padding: '12px 16px 12px 44px',
                  background: '#faf7f2',
                  border: '2px solid #f0ebe0',
                  borderRadius: '14px',
                  fontSize: '15px',
                  color: '#1a2740',
                  outline: 'none',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#4a7c5e'
                  e.target.style.boxShadow = '0 0 0 4px rgba(74,124,94,0.1)'
                  e.target.style.background = 'white'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#f0ebe0'
                  e.target.style.boxShadow = 'none'
                  e.target.style.background = '#faf7f2'
                }}
              />
            </div>
          </div>
        </motion.div>

        {/* Conversations List */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="space-y-3"
        >
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse"
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '20px',
                  border: '1px solid #f0ebe0',
                }}
              >
                <div className="flex items-center gap-4">
                  <div style={{ width: '52px', height: '52px', background: '#f0ebe0', borderRadius: '14px' }} />
                  <div className="flex-1">
                    <div style={{ height: '18px', background: '#f0ebe0', borderRadius: '8px', width: '70%', marginBottom: '8px' }} />
                    <div style={{ height: '14px', background: '#f0ebe0', borderRadius: '6px', width: '40%' }} />
                  </div>
                </div>
              </div>
            ))
          ) : filteredConversations.length === 0 ? (
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '60px 24px',
                textAlign: 'center',
                boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
                border: '1px solid #f0ebe0',
              }}
            >
              <div
                className="mx-auto mb-6 flex items-center justify-center"
                style={{
                  width: '80px', height: '80px',
                  background: 'linear-gradient(135deg, #4a7c5e 0%, #2d5a3d 100%)',
                  borderRadius: '24px',
                  boxShadow: '0 12px 40px rgba(74,124,94,0.3)',
                  fontSize: '40px',
                }}
              >
                💬
              </div>
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#1a2740', marginBottom: '8px' }}>
                No conversations found
              </h3>
              <p style={{ fontSize: '16px', color: '#4a5568', marginBottom: '24px' }}>
                {searchTerm || selectedCategory !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Start a new conversation to see it here.'
                }
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  background: '#4a7c5e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '14px 28px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(74,124,94,0.3)',
                }}
              >
                Start New Conversation
              </button>
            </motion.div>
          ) : (
            <AnimatePresence>
              {filteredConversations.map((conversation, index) => (
                <motion.div
                  key={conversation.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ delay: index * 0.04 }}
                  onClick={() => handleConversationClick(conversation.id)}
                  className="group cursor-pointer"
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '18px 20px',
                    border: '2px solid transparent',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#b8d9c4'
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(74,124,94,0.12)'
                    e.currentTarget.style.transform = 'translateY(-2px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'transparent'
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }}
                >
                  <div className="flex items-center gap-4">
                    {/* Category Icon */}
                    <div
                      className="flex-shrink-0 flex items-center justify-center"
                      style={{
                        width: '52px', height: '52px',
                        background: 'linear-gradient(135deg, #4a7c5e 0%, #2d5a3d 100%)',
                        borderRadius: '14px',
                        fontSize: '24px',
                        boxShadow: '0 4px 12px rgba(74,124,94,0.25)',
                        transition: 'transform 0.3s',
                      }}
                    >
                      {conversation.categories?.icon}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      {editingId === conversation.id ? (
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            style={{
                              flex: 1,
                              border: '2px solid #4a7c5e',
                              borderRadius: '8px',
                              padding: '6px 10px',
                              fontSize: '15px',
                              color: '#1a2740',
                              outline: 'none',
                            }}
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(conversation); if (e.key === 'Escape') setEditingId(null) }}
                            autoFocus
                          />
                          <Button size="sm" variant="secondary" onClick={() => saveTitle(conversation)}>Save</Button>
                        </div>
                      ) : (
                        <h3
                          className="truncate"
                          style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#1a2740',
                            marginBottom: '4px',
                            transition: 'color 0.2s',
                          }}
                        >
                          {lastUserSnippets[conversation.id] || conversation.title}
                        </h3>
                      )}
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                        <span className="flex items-center gap-1.5" style={{ fontSize: '13px', color: '#4a7c5e', fontWeight: '500' }}>
                          <span style={{ width: '6px', height: '6px', background: '#4a7c5e', borderRadius: '50%', display: 'inline-block' }} />
                          {conversation.categories?.name}
                        </span>
                        <span className="flex items-center gap-1" style={{ fontSize: '13px', color: '#4a5568', opacity: 0.6 }}>
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(conversation.updated_at)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {editingId !== conversation.id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); startEditing(conversation) }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block"
                          style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: '1px solid #f0ebe0',
                            background: '#faf7f2',
                            color: '#4a5568',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                          }}
                        >
                          Rename
                        </button>
                      )}
                      <div
                        className="hidden sm:flex items-center justify-center"
                        style={{
                          width: '36px', height: '36px',
                          borderRadius: '10px',
                          background: '#faf7f2',
                          color: '#4a5568',
                          transition: 'all 0.2s',
                        }}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </motion.div>

        {/* Stats footer */}
        {filteredConversations.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{
              marginTop: '24px',
              background: 'white',
              borderRadius: '14px',
              padding: '16px 20px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              border: '1px solid #f0ebe0',
            }}
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2" style={{ fontSize: '14px', color: '#4a5568' }}>
              <span style={{ background: '#faf7f2', padding: '6px 14px', borderRadius: '50px' }}>
                Showing <span style={{ fontWeight: '700', color: '#1a2740' }}>{filteredConversations.length}</span> of {conversations.length} conversations
              </span>
              <span className="flex items-center gap-1.5" style={{ background: '#faf7f2', padding: '6px 14px', borderRadius: '50px' }}>
                <Calendar className="w-3.5 h-3.5" />
                Last updated: <span style={{ fontWeight: '700', color: '#1a2740' }}>{conversations[0] ? formatDate(conversations[0].updated_at) : 'Never'}</span>
              </span>
            </div>
          </motion.div>
        )}
      </div>

      {/* Hidden Categories Modal */}
      <AnimatePresence>
        {showHiddenModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.4)' }}
            onClick={() => setShowHiddenModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '24px',
                maxWidth: '420px',
                width: '100%',
                boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
              }}
            >
              <div className="flex items-center justify-between mb-5">
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1a2740' }}>
                  Manage Hidden Categories
                </h3>
                <button
                  onClick={() => setShowHiddenModal(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4a5568' }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {hiddenCategories.length === 0 ? (
                <p style={{ color: '#4a5568', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>
                  No hidden categories.
                </p>
              ) : (
                <div className="space-y-2">
                  {hiddenCategories.map((cat) => (
                    <div
                      key={cat.id}
                      className="flex items-center justify-between"
                      style={{
                        padding: '12px 14px',
                        background: '#faf7f2',
                        borderRadius: '12px',
                        border: '1px solid #f0ebe0',
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <span style={{ fontSize: '20px' }}>{cat.icon}</span>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#1a2740' }}>{cat.name}</span>
                      </div>
                      <button
                        onClick={() => unhideCategory(cat.id)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          background: '#e8f2ec',
                          border: '1px solid #b8d9c4',
                          color: '#2d5a3d',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                        }}
                      >
                        Unhide
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
